import Vue from 'vue';
import Builder from './dragdrop/Builder';
import DragOverlay from './dragdrop/DragOverlay';
import CanvasController from './elements/CanvasController';
import ConfirmCanvasAction from './elements/ConfirmCanvasAction';
import DomTree from './elements/DomTree';
import textutils from './elements/textUtils';
import StyleController from './style-editor/StyleController';
import StyleReader from './style-editor/StyleReader';
import styleWriter from './style-editor/stylewriter';
import utils from './utils';

const eventBus = new Vue();
const draggable = new Builder(eventBus);
const controller = new CanvasController(eventBus);

const canvas = new Vue({
  data: {
    styleOptions: {
      properties: {},
      breakpoints: {},
      states: []
    },
    styleController: null,
    selected: null,
    loadedStyleId : null,
    styles: {},
    eventBus,
    draggable,
    controller,
    menus: draggable.menus
  },
  computed: {
    breakpointLabels() {
      return utils.breakpointLabels(this.styleOptions.breakpoints);
    },
    styleStateVariants() {
      return ['none', ...this.styleOptions.states];
    }
  },
  created() {
    controller.parent = this;
    this.eventBus.$on('edit-text', data => {
      setTimeout(() => {
        this.frame.contentWindow.quillEdit(
          data.element,
          this.styleOptions,
          this.eventBus
        );
      });
    });
    this.eventBus.$on('component-cloned', data => {
      this.copyElementStyles(data);
      this.copyCustomStyles(data);
    });
    this.eventBus.$on('text-node-cloned', data => {
      this.copyTextStyles(data);
      this.copyCustomStyles(data);
    });
    this.eventBus.$on('canvas-change', () => {
      this.refresh().then(() => this.eventBus.$emit('canvas-post-change'));
    });
    this.eventBus.$on('text-selection-changed', () => {
      this.getElementStyles(this.selected);
      this.eventBus.$emit('select-element', this.selected);
    });
    this.eventBus.$on('select-element-request', data => {
      if (!data) this.loadedStyleId = null;
      const id = data && (data.component || data);
      if (!this.selected && !id) return;
      if (this.selected) {
        if (id === this.selected.id) return;
        this.selected.setTextPlaceholder();
        if (this.selected.element && this.selected.textEditable) textutils.destroyEditor(this.selected.element);
        utils.cleanCanvas(this.selected.element,true);
      }
      const element = id && utils.getComponent(id, this.eventBus, this.frame.contentWindow.document);
      
      if (data && data.targetedElement) element.childrenSelected = [data.targetedElement];
      this.selected = element;
      if (element && element.textEditable) element.editText();
      if (element) this.styleController = new StyleController(this.frame.contentWindow.document, element);

      this.eventBus.$emit('select-element', element);
    });
    const receiveMessage = event => {
      if (event.data === 'block-drag-start') {
        this.eventBus.$emit('select-element-request', null);
        this.eventBus.$emit('canvas-drag:start', { type: 'blocks' });
      }
      if (event.data === 'element-drag-start') {
        this.eventBus.$emit('select-element-request', null);
        this.eventBus.$emit('canvas-drag:start', { type: 'elements' });
      }
      if (event.data === 'block-drag-stop') {
        this.eventBus.$emit('canvas-drag:stop', { type: 'blocks' });
        this.refresh();
      }
      if (event.data === 'element-drag-stop') {
        this.eventBus.$emit('canvas-drag:stop', { type: 'elements' });
        this.refresh();
      }
    };
    window.addEventListener('message', receiveMessage, false);
  },
  methods: {
    visibleBlock() {
      const blocks = this.frame.contentDocument.body.querySelectorAll(`[data-gp-block-id]`);

      var visibleBlock = null;

      for (const block of blocks) {
        if( this.isInViewport(block) ){
          visibleBlock = block;
          break;
        }
      }

      return visibleBlock;
    },
    isInViewport(element) {
      const rect = element.getBoundingClientRect();
      return rect.top >= 0;
    },
    getFrame() {
      return this.frame;
    },
    setStyle(property, value, options) {
      this.styleController.setStyle(property, value, options);
      this.writeStyles();
    },
    softSetStyle(property, value, options) {
      this.styleController.setStyle(property, value, options);
    },
    getStyle(property, options = {}) {
      return this.styleController.getStyle(property, options);
    },
    writeStyles(values) {
      this.styleController.setStyles(values);
      this.styleController.values = JSON.parse(
        JSON.stringify(values || this.styleController.values)
      );
      this.styles = this.styleController.values;
      this.emit('canvas-style-update');
    },
    linkFrame(frame) {
      if (!frame) frame = this.frame;
      this.frame = frame;
      this.draggable.linkFrame(frame);
      if(frame.contentWindow) {
        this.controller.setCanvas(frame.contentWindow.document);
      }
    },
    refresh(blockAdders = true) {
      return new Promise(resolve => {
        if (!this.frame) return;
        this.draggable.refresh();
        utils
          .reinitializeScripts(this.frame.contentWindow.document)
          .then(() => {
            utils.wrapElements(this.frame.contentWindow.document);
            utils.clearOrphanedStyles(this.frame.contentWindow.document);
            utils.setupComponents(this.frame.contentWindow.document);
            if (blockAdders) utils.setupBlockAdders(this.frame.contentWindow.document);
            this.controller.refresh();
            resolve();
          });
      });
    },
    getStylesheets({id:elementId,childrenSelected:childIds}) {
      return [...this.frame.contentWindow.document.styleSheets].filter(
        stylesheet => ( !stylesheet.href && [elementId,...childIds].map(id => (`style-${id}`)).includes(stylesheet.ownerNode.className) ) || (stylesheet.href && stylesheet.href.includes(window.gdEnv.VUE_APP_FRONTEND_URL))
      );
    },
    getElementStyles(element) {
      if( element.id !== this.loadedStyleId ) {
        this.loadedStyleId = element.id;
        this.styleController = new StyleController(
          this.frame.contentWindow.document,
          element
        );
        this.styles = this.styleController.getStyles(
          this.styleOptions.properties,
          this.styleOptions.breakpoints,
          this.styleOptions.states,
          this.getStylesheets(element)
        );
      }
    },
    getStyleController(element) {
      return new StyleController(this.frame.contentWindow.document, element);
    },
    setStyleOptions(options = {}) {
      this.styleOptions.properties =
        options.properties || this.styleOptions.properties;
      this.styleOptions.breakpoints =
        options.breakpoints || this.styleOptions.breakpoints;
      this.styleOptions.states = options.states || this.styleOptions.states;
    },
    copyElementStyles(data) {
      const oldComponent = utils.getComponent(
        data.from,
        this.eventBus,
        this.frame.contentWindow.document
      );
      const newComponent = utils.getComponent(
        data.to,
        this.eventBus,
        this.frame.contentWindow.document
      );
      const oldStyles = this.getStyleController(oldComponent);
      oldStyles.getStyles(
        this.styleOptions.properties,
        this.styleOptions.breakpoints,
        this.styleOptions.states,
        this.getStylesheets(oldComponent)
      );
      const newStyles = this.getStyleController(newComponent);
      newStyles.setStyles(oldStyles.values);
    },
    copyCustomStyles({from,to}) {
      const oldCustomCss = this.frame.contentWindow.document.querySelector(`.style-${from}-custom-css`);
      if (!oldCustomCss) return;
      const content = oldCustomCss.innerHTML.replaceAll(from, to);
      this.styleController.updateStylesheet(
        content,
        `style-${to}-custom-css`,
        to
      );
    },
    copyTextStyles(data) {
      if (
        !this.frame.contentWindow.document.querySelector(
          `.gp-component-id-${data.from}`
        )
      )
        return;
      const oldElement = this.frame.contentWindow.document
        .querySelector(`.gp-component-id-${data.from}`)
        .parentNode.parentNode.cloneNode(true)
        .querySelector(`.gp-component-id-${data.from}`);
      const styleReader = new StyleReader(
        this.styleOptions.breakpoints,
        this.styleOptions.states,
        this.styleOptions.properties
      );
      const stylesheets = [
        ...this.frame.contentWindow.document.styleSheets
      ].filter(
        stylesheet =>
          stylesheet.ownerNode &&
          stylesheet.ownerNode.getAttribute('data-gp-styled-element') ===
            data.from
      );
      const values = styleReader.getStyles(oldElement, stylesheets);
      const content = styleWriter.getStylesheet(
        values,
        `.gp-component-id-${data.to}`
      );
      this.styleController.updateStylesheet(
        content,
        `style-${data.to}`,
        data.to
      );
    },
    addMenu(config) {
      this.draggable.addMenu(config);
    },
    zoomOut() {
      this.draggable.zoomOut();
    },
    zoomIn() {
      this.draggable.zoomIn();
    },
    getComponent(id) {
      return utils.getComponent(
        id,
        this.eventBus,
        this.frame.contentWindow.document
      );
    },
    on(event, callback) {
      this.eventBus.$on(event, callback);
    },
    off(event, callback) {
      this.eventBus.$off(event, callback);
    },
    emit(event, callback) {
      this.eventBus.$emit(event, callback);
    }
  }
});

export default {
  install(Vue) {
    Vue.component('DomTree', DomTree);
    Vue.component('DragOverlay', DragOverlay);
    Vue.component('ConfirmCanvasAction', ConfirmCanvasAction);
    Vue.prototype.$canvas = canvas;
  }
};
